import { Button, Flex, Icon } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import classNames from "classnames/dedupe";
import { Summary } from "./Summary";
import { HelpLink } from "../Section/Question/HelpLink";
import { rightArrow } from "@simplybusiness/icons";

type DetailsProps = {
  id: string;
  header: string;
  children: React.ReactNode;
  isOpen: boolean;
  isConfirmed: boolean;
  onReOpen: (assumptionGroup: number) => void;
  onConfirm: (assumptionGroup: number) => void;
  assumptionGroupIndex: number;
  helpText?: string;
};

export const Details = ({
  id,
  header,
  isOpen,
  isConfirmed,
  onReOpen,
  onConfirm,
  assumptionGroupIndex,
  children,
  helpText,
}: DetailsProps) => {
  const { snowplow } = useSnowplowContext();

  const isCollapsed = isConfirmed && !isOpen;
  const showHelpButton = helpText !== "" && isOpen;

  const trackButtonClick = (source: "Review" | "More Info" | "Confirm") => {
    snowplow?.trigger("assumptionsButtonClicked", {
      source,
      presentationGroup: header,
    });
  };

  const handleReview = () => {
    trackButtonClick("Review");
    onReOpen(assumptionGroupIndex);
  };

  const handleConfirm = () => {
    trackButtonClick("Confirm");
    onConfirm(assumptionGroupIndex);
  };

  const handleHelpOpen = () => {
    trackButtonClick("More Info");
  };

  return (
    <div className="mobius-collapsible__wrapper">
      <div className="mobius-collapsible__external-buttons">
        {isCollapsed && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleReview}
            className="mobius-collapsible__review-button"
          >
            Review
          </Button>
        )}
        {showHelpButton && (
          <HelpLink
            headline={header}
            label="More info"
            text={helpText ?? ""}
            displayAsButton
            onOpenDrawer={handleHelpOpen}
          />
        )}
      </div>
      <div
        id={id}
        role="group"
        aria-label={header}
        aria-expanded={isOpen}
        className={classNames("mobius-collapsible__details", {
          "--is-open": isOpen,
        })}
        style={{ viewTransitionName: id }}
      >
        <Summary header={header} isCollapsed={isCollapsed} />
        <div className="mobius-collapsible__body" inert={!isOpen || undefined}>
          <div className="mobius-collapsible__body-inner">
            <div className="mobius-collapsible__content">{children}</div>
            <Flex gap="sm" className="mobius-collapsible__actions">
              <Button variant="primary" size="sm" onClick={handleConfirm}>
                <span>Confirm</span>
                <Icon icon={rightArrow} title="Proceed to next step" />
              </Button>
            </Flex>
          </div>
        </div>
      </div>
    </div>
  );
};
